<template>
  <v-dialog v-model="dialog" transition="dialog-bottom-transition" scrollable max-width="560">
    <template v-if="!hideButton" v-slot:activator="{ props }">
      <v-btn
        color="primary"
        :class="{ 'float-right': floatRight, 'mx-2': floatRight }"
        v-bind="props"
      >
        <v-icon class="mr-2">mdi-account-plus</v-icon>
        Atribuir vendedor
      </v-btn>
    </template>
    <v-card>
      <v-toolbar color="primary" :title="toolbarTitle">
        <v-btn color="black" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              v-model="form.user_id"
              v-model:search="userSearch"
              :items="userItems"
              item-title="title"
              item-value="value"
              label="Usuário"
              :rules="[(v) => !!v || 'Campo obrigatório']"
              :loading="loadingUsers"
              :disabled="requestingApi || isEdit"
              density="compact"
              clearable
              hide-details="auto"
              no-filter
              :no-data-text="userNoDataText"
            >
              <template v-slot:no-data>
                <div class="px-4 py-2 text-medium-emphasis">
                  {{ userNoDataText }}
                </div>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.role"
              :items="roleItems"
              item-title="title"
              item-value="value"
              label="Função"
              :rules="[(v) => !!v || 'Campo obrigatório']"
              :disabled="requestingApi"
              density="compact"
              hide-details="auto"
            ></v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-switch
              color="green"
              :label="form.active ? 'Ativo' : 'Inativo'"
              v-model="form.active"
              :disabled="requestingApi"
              hide-details
            ></v-switch>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn variant="text" color="error" @click="dialog = false">Cancelar</v-btn>
        <v-btn
          color="success"
          :loading="requestingApi"
          :disabled="requestingApi"
          @click="submit"
        >
          <v-icon class="mr-2">mdi-content-save</v-icon>
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import api from '@/services'
import { normalizeListPayload } from '@/utils/listPayload'

const ROLE_ITEMS = [
  { title: 'Vendedor', value: 'seller' },
  { title: 'Gerente', value: 'manager' },
  { title: 'Validador de ingresso', value: 'ticket_checker' },
]

const ALLOWED_ROLES = new Set(['seller', 'manager', 'ticket_checker'])

function normalizeRole(role) {
  return ALLOWED_ROLES.has(role) ? role : 'seller'
}

function emptyForm(storeId) {
  return {
    id: null,
    store_id: storeId != null ? Number(storeId) : null,
    user_id: null,
    role: 'seller',
    active: true,
    user: null,
  }
}

function userLabel(u) {
  if (!u) return '—'
  const name = u.name || u.legal_person?.name || ''
  const email = u.email || ''
  if (name && email) return `${name} (${email})`
  return name || email || `Usuário #${u.id}`
}

export default {
  props: {
    user: Object,
    storeId: {
      type: [Number, String],
      required: true,
    },
    modelValue: Object,
    floatRight: Boolean,
    hideButton: Boolean,
  },
  emits: ['update:modelValue', 'created', 'updated'],
  data() {
    return {
      dialog: false,
      requestingApi: false,
      loadingUsers: false,
      users: [],
      userSearch: '',
      searchTimeout: null,
      roleItems: ROLE_ITEMS,
      form: emptyForm(this.storeId),
    }
  },
  computed: {
    isEdit() {
      return !!(this.form?.id || this.modelValue?.id)
    },
    toolbarTitle() {
      return this.isEdit ? 'Editar vendedor da loja' : 'Atribuir vendedor'
    },
    userItems() {
      const items = (this.users || []).map((u) => ({
        title: userLabel(u),
        value: u.id,
        user: u,
      }))
      const current = this.form?.user || this.modelValue?.user
      if (current?.id && !items.some((i) => i.value === current.id)) {
        items.unshift({ title: userLabel(current), value: current.id, user: current })
      }
      return items
    },
    userNoDataText() {
      if (this.loadingUsers) return 'Carregando…'
      if (!api.storage.isGroupSelected()) return 'Selecione um grupo para buscar'
      if (!(this.userSearch || '').trim()) return 'Digite para buscar usuários do grupo'
      return 'Nenhum usuário do grupo encontrado'
    },
  },
  methods: {
    resetForm() {
      this.form = emptyForm(this.storeId)
      this.users = []
      this.userSearch = ''
    },
    hydrateFromModel(model) {
      if (!model || (!model.id && !model.user_id)) {
        this.resetForm()
        return
      }
      this.form = {
        id: model.id || null,
        store_id: Number(this.storeId),
        user_id: model.user_id || model.user?.id || null,
        role: normalizeRole(model.role),
        active: model.active !== false,
        user: model.user || null,
      }
      if (model.user) {
        this.users = [model.user]
      }
    },
    scheduleUserSearch(text) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = null
      }
      const q = (text || '').trim()
      if (!q) {
        this.loadingUsers = false
        return
      }
      this.loadingUsers = true
      this.searchTimeout = setTimeout(() => {
        this.searchTimeout = null
        this.loadUsers(q)
      }, 400)
    },
    /**
     * Group-scoped user search for seller assignment.
     * Uses legalPerson.list (member_group_id + withUser) — not user.list (platform-wide, 777-only).
     */
    loadUsers(searchText) {
      const vm = this
      const q = (searchText || '').trim()
      if (!q) {
        vm.loadingUsers = false
        return Promise.resolve()
      }
      const groupId = api.storage.selectedGroup()?.id
      if (!groupId) {
        vm.users = []
        vm.loadingUsers = false
        vm.$notify({ type: 'warn', text: 'Selecione um grupo para buscar vendedores' })
        return Promise.resolve()
      }
      vm.loadingUsers = true
      return api.legalPerson
        .list(
          {
            searchText: q,
            withUser: true,
            itemsPerPage: 30,
            member_group_id: Number(groupId),
            active: true,
          },
          1
        )
        .then((result) => {
          const { items } = normalizeListPayload(result)
          const list = items?.length
            ? items
            : Array.isArray(result?.data)
              ? result.data
              : Array.isArray(result)
                ? result
                : []
          // Store sellers need a system user_id; LP may exist without a login.
          vm.users = list
            .filter((lp) => lp?.user_id || lp?.user?.id)
            .map((lp) => {
              const u = lp.user || {}
              return {
                id: u.id || lp.user_id,
                name: u.name || lp.name || '',
                email: u.email || '',
                legal_person: lp,
              }
            })
        })
        .catch((err) => {
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          vm.loadingUsers = false
        })
    },
    buildPayload() {
      const f = this.form || {}
      const payload = {
        store_id: Number(this.storeId),
        user_id: f.user_id != null ? Number(f.user_id) : null,
        role: normalizeRole(f.role),
        active: f.active !== false,
      }
      if (f.id) {
        payload.id = f.id
      }
      return payload
    },
    submit() {
      const vm = this
      if (vm.requestingApi) return
      if (!vm.form?.user_id) {
        vm.$notify({ type: 'warn', text: 'Selecione um usuário' })
        return
      }
      if (!vm.storeId) {
        vm.$notify({ type: 'warn', text: 'Loja inválida' })
        return
      }
      if (!vm.form.role) {
        vm.$notify({ type: 'warn', text: 'Selecione a função' })
        return
      }
      vm.requestingApi = true
      const wasEdit = vm.isEdit
      return api.storeUser
        .updateOrCreate(vm.buildPayload())
        .then((result) => {
          if (wasEdit) {
            vm.$emit('updated', result)
            vm.$notify({ type: 'success', text: 'Vendedor atualizado' })
          } else {
            vm.$emit('created', result)
            vm.$notify({ type: 'success', text: 'Vendedor atribuído à loja' })
          }
          vm.dialog = false
        })
        .catch((err) => {
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          vm.requestingApi = false
        })
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal && (newVal.id || newVal.user_id) && !this.dialog) {
        this.hydrateFromModel(newVal)
        this.dialog = true
      }
    },
    dialog(newVal) {
      if (newVal) {
        if (this.modelValue && (this.modelValue.id || this.modelValue.user_id)) {
          this.hydrateFromModel(this.modelValue)
        } else if (!this.form?.user_id) {
          this.resetForm()
        }
      } else {
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout)
          this.searchTimeout = null
        }
        this.resetForm()
        this.$emit('update:modelValue', {})
      }
    },
    userSearch(newVal) {
      if (!this.dialog || this.isEdit) return
      this.scheduleUserSearch(newVal)
    },
    storeId(newVal) {
      if (this.form) {
        this.form.store_id = newVal != null ? Number(newVal) : null
      }
    },
  },
  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
}
</script>
